import React, { ReactElement, ReactNode } from 'react'
import { StyledEngineProvider } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'

interface Props {
  children: ReactNode
}

type FW = {
  [key in '100' | '300' | '400' | '500' | '700' | '900']: number
}

export const Colors = {
  white: '#FFFFFF',
  black: '#000000',
  error: '#DC7C7C',
  dark: '#3E3E3E',
  darkGrey: '#383a41',
  yellow: '#FFFF00',
}

export const fontWeights: FW = {
  '100': 100,
  '300': 300,
  '400': 400,
  '500': 500,
  '700': 700,
  '900': 900,
}

export const ThemeConfig = ({ children }: Props): ReactElement => {
  const theme = createTheme({
    typography: {
      h1: {
        fontSize: 32,
        lineHeight: 1.25,
        color: Colors.white,
        fontWeight: fontWeights['400'],
      },
      h2: {
        fontSize: 28,
        fontWeight: 500,
        lineHeight: 1.25,
        opacity: '0.87',
        color: Colors.white,
      },
      h3: {
        fontSize: 26,
        fontWeight: 500,
        lineHeight: 1.25,
        opacity: '0.87',
        color: Colors.white,
      },
      h4: {
        fontSize: 22,
        fontWeight: 500,
        lineHeight: 1.25,
        opacity: '0.87',
        color: Colors.white,
      },
      h5: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: 1.25,
        opacity: '0.87',
        color: Colors.white,
      },
      h6: {
        fontSize: 18,
        fontWeight: 500,
        lineHeight: 1.25,
        opacity: '0.87',
        color: Colors.white,
      },
      subtitle1: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.25,
        opacity: '0.6',
        color: Colors.white,
      },
    },
    palette: {
      primary: {
        main: Colors.black, // theme.palette.primary.main
        light: Colors.white, // theme.palette.primary.light
        dark: Colors.dark, // theme.palette.primary.dark
      },
      secondary: {
        main: '#51E5A5', // theme.palette.secondary.main
        light: '#6BFFBF', // theme.palette.secondary.light
      },
      error: {
        main: Colors.error, // theme.palette.error.main
      },
      info: {
        main: '#292C32', // theme.palette.info.main
        light: '#383A40', // theme.palette.info.light
        dark: '#43454A', // theme.palette.info.dark
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1140,
        xl: 1920,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            background: '#43454A',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.6)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.white,
              borderWidth: 1,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#51E5A5',
              borderWidth: 1,
            },
          },
          input: {
            color: `${Colors.white}`,
            opacity: '0.87',
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px #43454a inset',
              WebkitTextFillColor: `${Colors.white}`,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: Colors.error,
            '&$error': {
              color: Colors.error,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: Colors.dark,
            fontSize: 16,
          },
          asterisk: {
            color: '#51E5A5',
            '&$error': {
              color: '#51E5A5',
            },
          },
        },
      },
    },
  })

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </StyledEngineProvider>
  )
}
